import { Column, RowData, Table } from '@tanstack/react-table';
import { Input } from '../../../Input';
import { FloatingLabelProps } from '../../../FloatingLabel';
import { ColumnFilterCombobox } from './ColumnFilterCombobox';
import { Fragment } from 'react';
import { ColumnDefMeta } from '../../types';
import useI18n from '../../../../hooks/useI18n';

interface TableCellFilterProps<TData extends RowData, TValue> {
  table: Table<TData>;
  columns: Column<TData, TValue>[];
}

export function CellFilter<TData, TValue>({
  table,
  columns,
}: TableCellFilterProps<TData, TValue>) {
  const i18n = useI18n();

  return (
    <div className='eye:flex eye:flex-col eye:gap-16'>
      {columns.map((column, index) => {
        const variant =
          (column.columnDef?.meta as ColumnDefMeta)?.filterVariant ?? 'text';
        if (!column.getCanFilter() || !column.getIsVisible()) {
          return null;
        }
        const filterValue = column.getFilterValue();
        const defaultLabelProps: FloatingLabelProps = {
          label: column.columnDef.header?.toString(),
          float: 'top',
        };

        return (
          <Fragment key={index}>
            {variant === 'range' ? (
              <div className='eye:flex eye:gap-16'>
                <Input
                  labelProps={{
                    ...defaultLabelProps,
                    label:
                      defaultLabelProps.label?.toString() +
                      ' ' +
                      String(i18n.t('from')),
                  }}
                  showClearButton={true}
                  type='number'
                  placeholder={i18n.t('value')}
                  value={(filterValue as [number, number])?.[0] ?? ''}
                  onChange={(e) => {
                    column.setFilterValue((old: [number, number] = [0, 0]) => {
                      const next: [number | undefined, number | undefined] = [
                        e.target.value ? Number(e.target.value) : undefined,
                        old?.[1],
                      ];

                      return next[0] == null && next[1] == null
                        ? undefined
                        : next;
                    });
                  }}
                />
                <Input
                  labelProps={{
                    ...defaultLabelProps,
                    label:
                      defaultLabelProps.label?.toString() +
                      ' ' +
                      String(i18n.t('to')),
                  }}
                  showClearButton={true}
                  type='number'
                  placeholder={i18n.t('value')}
                  value={(filterValue as [number, number])?.[1] ?? ''}
                  onChange={(e) => {
                    column.setFilterValue((old: [number, number] = [0, 0]) => {
                      const next: [number | undefined, number | undefined] = [
                        old?.[0],
                        e.target.value ? Number(e.target.value) : undefined,
                      ];

                      return next[0] == null && next[1] == null
                        ? undefined
                        : next;
                    });
                  }}
                />
              </div>
            ) : variant.includes('select') ? (
              <ColumnFilterCombobox table={table} columnId={column.id} />
            ) : (
              <Input
                labelProps={defaultLabelProps}
                showClearButton={true}
                placeholder={i18n.t('value')}
                value={(filterValue as string) ?? ''}
                onChange={(e) => column.setFilterValue(e.target.value)}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
